/**
 * User preferences for the Blogs page (view mode, items per page, theme).
 * Persisted per logged-in user in localStorage.
 */

const BLOGS_PREFS_PREFIX = "blogs_user_preferences_";
const AUTH_USER_ID_KEY = "auth_user_id";

const VALID_VIEW_MODES = ["classic", "grid", "list"] as const;
const VALID_THEME_MODES = ["light", "dark"] as const;
const VALID_ITEMS_PER_PAGE = [3, 5, 10, 15, 20];

export type BlogsViewMode = "classic" | "grid" | "list";
export type ThemeMode = "light" | "dark";

export interface BlogsUserPreferences {
  viewMode?: BlogsViewMode;
  itemsPerPage?: number;
  themeMode?: ThemeMode;
}

export function getBlogsPrefsKey(userId: string): string {
  return `${BLOGS_PREFS_PREFIX}${userId}`;
}

export function getBlogsUserPreferences(userId: string): BlogsUserPreferences | null {
  if (!userId || typeof userId !== "string") return null;
  try {
    const raw = localStorage.getItem(getBlogsPrefsKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const prefs: BlogsUserPreferences = {};
    if (VALID_VIEW_MODES.includes(parsed.viewMode as BlogsViewMode)) {
      prefs.viewMode = parsed.viewMode as BlogsViewMode;
    }
    if (
      typeof parsed.itemsPerPage === "number" &&
      VALID_ITEMS_PER_PAGE.includes(parsed.itemsPerPage)
    ) {
      prefs.itemsPerPage = parsed.itemsPerPage;
    }
    if (VALID_THEME_MODES.includes(parsed.themeMode as ThemeMode)) {
      prefs.themeMode = parsed.themeMode as ThemeMode;
    }
    return Object.keys(prefs).length > 0 ? prefs : null;
  } catch {
    return null;
  }
}

export function setBlogsUserPreferences(
  userId: string,
  prefs: Partial<BlogsUserPreferences>
): void {
  if (!userId || typeof userId !== "string") return;
  try {
    const key = getBlogsPrefsKey(userId);
    const existing = getBlogsUserPreferences(userId) || {};
    const next: BlogsUserPreferences = { ...existing };
    if (prefs.viewMode && VALID_VIEW_MODES.includes(prefs.viewMode)) {
      next.viewMode = prefs.viewMode;
    }
    if (
      prefs.itemsPerPage !== undefined &&
      VALID_ITEMS_PER_PAGE.includes(prefs.itemsPerPage)
    ) {
      next.itemsPerPage = prefs.itemsPerPage;
    }
    if (prefs.themeMode && VALID_THEME_MODES.includes(prefs.themeMode)) {
      next.themeMode = prefs.themeMode;
    }
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    // ignore
  }
}

/** Used by ThemeContext to read current user id without Redux */
export function getAuthUserIdFromStorage(): string | null {
  try {
    const id = localStorage.getItem(AUTH_USER_ID_KEY);
    return id && typeof id === "string" ? id : null;
  } catch {
    return null;
  }
}

/** Call after login to allow ThemeContext to use per-user theme */
export function setAuthUserIdInStorage(userId: string): void {
  try {
    if (userId) {
      localStorage.setItem(AUTH_USER_ID_KEY, userId);
    } else {
      localStorage.removeItem(AUTH_USER_ID_KEY);
    }
  } catch {
    // ignore
  }
}

/** Call on logout to clear stored user id */
export function clearAuthUserIdFromStorage(): void {
  try {
    localStorage.removeItem(AUTH_USER_ID_KEY);
  } catch {
    // ignore
  }
}
